import type { FormEvent, ReactElement, RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { AppFooter } from "@/app/layout/app-footer";
import { PublicHeader } from "@/app/layout/public-header";
import { ClmIcon } from "@/shared/components/icons/icon-map";
import { PublicBrandHeader } from "@/shared/components/public-brand-header";
import { SupportReference } from "@/shared/components/support-reference";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Input,
  Label,
} from "@/shared/components/ui";
import { digestFingerprint, useHeldIdempotencyKey } from "@/shared/utils/use-held-idempotency-key";
import {
  RETRY_AFTER_MAX_SECONDS,
  policyDisallowsEdgeWhitespace,
  type FirstCredentialCompleteRequest,
  type FirstCredentialSafeOutcome,
  type FirstCredentialValidationFailed,
} from "./first-credential-client";
import { PASSWORD_EDGE_WHITESPACE_MESSAGE, PasswordRequirements } from "./password-requirements";
import { secretFieldRef } from "./secret-field-ref";
import {
  commitFirstCredentialHandoffClaim,
  peekFirstCredentialHandoff,
  scrubFirstCredentialHandoffHolder,
  type FirstCredentialClaimTicket,
  type FirstCredentialHandoffHolder,
} from "./first-credential-memory-handoff";
import {
  useFirstCredentialCachePurge,
  useFirstCredentialCompletion,
  useFirstCredentialCompletionPurge,
  useFirstCredentialValidation,
} from "./use-first-credential";

/**
 * Standard-public /set-password page (CLMS-137 T024 US1). The fc1
 * authorization arrives ONLY through the one-read in-memory handoff:
 * peeked non-destructively during first render and consumed destructively
 * at commit (see FirstCredentialPage) — before any API call — so a reload,
 * hard navigation, new tab, session restore, or crash renders the generic
 * lost-session re-entry state instead of reconstructing authority. Password
 * requirements come from the server descriptor, the confirmation field is
 * client-only, completion is first-write-wins idempotent, and activation
 * ends with a user-activated Sign in — never an automatic redirect.
 */

const PASSWORD_REQUIREMENTS_ID = "first-credential-password-requirements";
const PASSWORD_ERROR_ID = "first-credential-password-error";
const CONFIRMATION_ERROR_ID = "first-credential-confirmation-error";

// Contract bound: PasswordValue maxLength is 128 (invitation-api-source
// OpenAPI, PasswordValue schema), counted in Unicode CODE POINTS. Enforced
// only at the submit gate: a native maxLength attribute would silently
// truncate pasted values at UTF-16 units — identically in both fields, so a
// truncated password-manager password would "match" and set a password the
// user doesn't have (form contract: never trim, normalize, or transform the
// password).
const PASSWORD_MAX_LENGTH = 128;

interface LastSubmission {
  request: FirstCredentialCompleteRequest;
  /** SHA-256 digest of the request (never the plaintext payload). */
  fingerprint: string;
}

/**
 * Non-secret snapshot of a validation_failed completion (message + field
 * errors only — never the password or authorization). Rendering the
 * recoverable error summary from this snapshot lets the completion
 * mutation — whose variables hold {authorization, password} — be reset and
 * evicted from the mutation cache immediately instead of retaining the
 * secrets until gcTime.
 */
interface ValidationFailedSnapshot {
  message: string;
  fieldErrors: FirstCredentialValidationFailed["fieldErrors"];
}

/** Contract outcomes that end the flow on this page (T029 terminal panels). */
type TerminalOutcome = FirstCredentialSafeOutcome["outcome"];

/**
 * Snapshot of the terminal outcome discriminator and its non-secret
 * correlation reference — never the message or code — so the panel (and
 * its support reference, where the contract requires one) survives the
 * immediate purge of the mutation cache.
 */
interface TerminalSnapshot {
  outcome: TerminalOutcome;
  correlationId: string;
}

/**
 * Completion outcomes that do NOT snapshot a terminal panel:
 * completed/validation_failed have their own recoverable surfaces, and
 * dependency_unavailable/rate_limited are the retryable classes (API
 * contract: authority unchanged, bounded user-activated retry honoring
 * Retry-After), so their in-memory state must survive.
 */
const NON_TERMINAL_COMPLETION_OUTCOMES: readonly string[] = [
  "completed",
  "validation_failed",
  "dependency_unavailable",
  "rate_limited",
];

/**
 * Validation (validate-time) outcomes that do NOT snapshot a terminal
 * panel: ready renders the form; rate_limited/dependency_unavailable occur
 * before the provider security decision, leave the fc1 authority unchanged,
 * and are retryable through explicit user activation (Retry-After hold).
 */
const NON_TERMINAL_VALIDATION_OUTCOMES: readonly string[] = [
  "ready",
  "rate_limited",
  "dependency_unavailable",
];

/**
 * Absolute wall-clock re-enable time for a Retry-After hold, DERIVED
 * synchronously from the settled retryable result (never armed in a passive
 * effect): the FIRST commit of a 429/503 result already renders its retry
 * action held — no fail-open window exists between the result rendering and
 * an effect landing the hold. Instances are compared by object identity, so
 * a release recorded for hold N can never pre-release hold N+1 even when
 * their deadlines coincide. Identity is keyed on the mutation ATTEMPT
 * generation, not the result object: TanStack can coalesce a retry's
 * pending→settled notifications into one committed render carrying the
 * same result object instance, so an object-identity key would retain the
 * already-released hold N and leave a fresh 429/503 unheld.
 */
interface RetryAfterHold {
  deadline: number;
  /** Mutation attempt that armed this hold (see interface doc). */
  generation: number;
}

function deriveRetryAfterHold(
  result: { outcome: string; retryAfterSeconds?: number } | undefined,
  generation: number,
): RetryAfterHold | null {
  if (result?.outcome !== "rate_limited" && result?.outcome !== "dependency_unavailable") {
    return null;
  }
  const retryAfterSeconds = result.retryAfterSeconds ?? 0;
  // Absent hint (optional on both 429 and 503): no hold — the immediate
  // explicit retry stays allowed.
  return retryAfterSeconds > 0
    ? { deadline: Date.now() + retryAfterSeconds * 1000, generation }
    : null;
}

export function FirstCredentialPage(): ReactElement {
  // Two-phase consumption (F4): the render-phase peek is NON-destructive —
  // react-router v7 wraps navigations in startTransition, so this render
  // can be started and abandoned without committing, and a destructive
  // render-phase read would consume the one-read handoff with nothing
  // committed to show for it. The peek stages the handoff into a stable
  // mutable HOLDER (shared by both fiber buffers — see the scrub note
  // below) and mints a claim ticket kept in a fiber-local ref; destructive
  // ownership is taken only by the commit-phase claim effect below. The
  // holder is scrubbable IN PLACE (F5): success and terminal states null
  // its fields so the fc1 bearer is unreachable from the current AND the
  // alternate fiber the moment the panel renders (UI contract: clear
  // authorization on success/expiry/revocation/unavailable/support, not
  // only on unmount).
  const claimTicketRef = useRef<FirstCredentialClaimTicket | null>(null);
  const holderRef = useRef<FirstCredentialHandoffHolder | null>(null);
  const [handoffPresent, setHandoffPresent] = useState<boolean>(() => {
    // StrictMode's double-invoked initializer shares this fiber's refs, so
    // the second invocation presents the first invocation's ticket and
    // receives the same staged holder.
    const peek = peekFirstCredentialHandoff(claimTicketRef.current);
    if (peek.ticket !== null) {
      claimTicketRef.current = peek.ticket;
    }
    holderRef.current = peek.holder;
    return peek.holder !== null;
  });
  // #141 (T036 follow-up): the two secret fields are UNCONTROLLED. A
  // controlled `useState` string is retained by BOTH fiber buffers (current +
  // alternate) plus the last committed props of every element it flowed
  // through, so a frozen BFCache entry (or the detached tree after unmount)
  // kept the typed password reachable on the heap even after setState("").
  // With refs, the only copy is the input node's own value, which
  // clearSecretFields() overwrites IN PLACE at every scrub point while the
  // form is mounted (pagehide, success, terminal). A real unmount (SPA
  // navigation, no pagehide) is covered separately: React detaches these
  // refs BEFORE the passive unmount cleanup runs, so clearSecretFields()
  // would find them already null there — the ref CLEANUP functions attached
  // to the two <Input>s (#141 R2) zero each node the moment it detaches.
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const confirmationInputRef = useRef<HTMLInputElement | null>(null);
  const [confirmationMismatch, setConfirmationMismatch] = useState(false);
  const [passwordServerError, setPasswordServerError] = useState<string | null>(null);
  const [validationFailedSnapshot, setValidationFailedSnapshot] =
    useState<ValidationFailedSnapshot | null>(null);
  const [terminal, setTerminal] = useState<TerminalSnapshot | null>(null);
  // Success is snapshotted too: the completed panel must survive the
  // success-time purge of the mutation cache (UI contract: clear sensitive
  // state on success, not only on unmount/terminal).
  const [completedSnapshot, setCompletedSnapshot] = useState(false);
  // Retry-After latches for the retryable pre-decision classes (mirrors the
  // parent registration page's RateLimit/Support panels). The holds
  // themselves are DERIVED below, not stored here.
  const [validateRateLimitCtx, setValidateRateLimitCtx] = useState<{
    correlationId: string;
  } | null>(null);
  const [validateOutageCtx, setValidateOutageCtx] = useState<{ correlationId: string } | null>(
    null,
  );
  // Release markers for the derived holds: a hold instance is "held" until
  // its own release marker lands (identity comparison — see RetryAfterHold).
  const [releasedValidateHold, setReleasedValidateHold] = useState<RetryAfterHold | null>(null);
  const [releasedCompletionHold, setReleasedCompletionHold] = useState<RetryAfterHold | null>(null);
  // Attempt generations: incremented at each mutate call site (never during
  // render — StrictMode-safe) so a retry that settles with the SAME result
  // object instance still derives a NEW hold (see RetryAfterHold).
  const [validateAttempt, setValidateAttempt] = useState(0);
  const [completionAttempt, setCompletionAttempt] = useState(0);

  const validation = useFirstCredentialValidation();
  const completion = useFirstCredentialCompletion();
  const idempotencyKeys = useHeldIdempotencyKey();
  const purgeMutationCache = useFirstCredentialCachePurge();
  const purgeCompletionMutations = useFirstCredentialCompletionPurge();
  const lastSubmissionRef = useRef<LastSubmission | null>(null);
  const validateStartedRef = useRef(false);
  const claimOwnedRef = useRef(false);
  const holderScrubGenerationRef = useRef(0);
  // Synchronous invalidation witness for the snapshot effects (T036 round 5).
  // If pagehide fired between a mutation result's COMMIT and that render's
  // passive effects, the stale effect closure would re-create the
  // completed/terminal snapshot AFTER invalidateSecureSession cleared it,
  // resurrecting a panel over the mandated lost-session state. React 19
  // currently flushes those passive effects synchronously with the commit
  // (useMutation results arrive on the useSyncExternalStore sync lane), so
  // this ref is defense-in-depth against those scheduler internals changing.
  // A boolean ref is sufficient (no generation counter): handoffPresent only
  // ever goes true→false on this mount — a new legitimate session requires a
  // remount, which gets a fresh ref.
  const sessionInvalidatedRef = useRef(false);
  // #141: one controller per in-flight validate/complete attempt. Aborted at
  // pagehide (invalidateSecureSession) and on unmount so a frozen or dead
  // document never leaves a request carrying fc1 (+ password) pending on the
  // network stack; also aborted when a newer attempt supersedes it.
  const validateAbortRef = useRef<AbortController | null>(null);
  const completionAbortRef = useRef<AbortController | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const validationAlertRef = useRef<HTMLDivElement | null>(null);
  const uncertainAlertRef = useRef<HTMLDivElement | null>(null);
  const rateLimitAlertRef = useRef<HTMLDivElement | null>(null);

  const { mutate: validateMutate, reset: validateReset } = validation;
  const { mutate: completionMutate, reset: completionReset } = completion;
  const { reset: resetIdempotencyKeys } = idempotencyKeys;

  // Overwrites the uncontrolled secret fields in place (see the ref
  // declarations). Null-safe: the form may already be unmounted.
  const clearSecretFields = useCallback((): void => {
    if (passwordInputRef.current) {
      passwordInputRef.current.value = "";
    }
    if (confirmationInputRef.current) {
      confirmationInputRef.current.value = "";
    }
  }, []);

  // Overwrites the held submission's secrets IN PLACE, then drops it (#141
  // R2/R15). The request object is still referenced by the transport call
  // and the mutation variables, so nulling the ref alone would leave fc1 +
  // password reachable from there. The single scrub path for every site that
  // ends or supersedes the replay window. Null-safe: nothing may be held.
  const scrubLastSubmission = useCallback((): void => {
    const last = lastSubmissionRef.current;
    if (last === null) {
      return;
    }
    last.request.authorization = "";
    last.request.password = "";
    lastSubmissionRef.current = null;
  }, []);

  // Aborts whatever validate/complete attempt is in flight (no-op when none).
  const abortInFlightRequests = useCallback((): void => {
    validateAbortRef.current?.abort();
    validateAbortRef.current = null;
    completionAbortRef.current?.abort();
    completionAbortRef.current = null;
  }, []);

  // Arms a fresh controller for one attempt, aborting a superseded one.
  // Memoized so the validate effect below can list it as a dependency
  // honestly (it would otherwise re-run the one-shot validate every render).
  const armAbort = useCallback((ref: RefObject<AbortController | null>): AbortSignal => {
    ref.current?.abort();
    const controller = new AbortController();
    ref.current = controller;
    return controller.signal;
  }, []);

  // React 19 ref callbacks with cleanup (#141 R2): detaching the node (real
  // unmount, no pagehide) overwrites its value in place, so the detached DOM
  // subtree never carries the typed secret. React runs the cleanup whenever
  // the callback IDENTITY changes, so an inline callback would wipe the field
  // on every re-render. Held in `useState` initializers rather than `useMemo`:
  // React documents useMemo as a cache it may discard, whereas state is
  // guaranteed stable for the life of the mount.
  const [passwordFieldRef] = useState(() => secretFieldRef(passwordInputRef));
  const [confirmationFieldRef] = useState(() => secretFieldRef(confirmationInputRef));

  // V9 (fail-closed): both Retry-After holds are derived synchronously from
  // the settled result data itself, so the first render of a retryable
  // 429/503 outcome already shows the retry action held. The effects further
  // down only RELEASE a hold once its deadline passes. The attempt
  // generation in the deps re-keys the derivation per settled attempt even
  // when consecutive settlements share one result object instance (V12).
  const validateHold = useMemo(
    () => deriveRetryAfterHold(validation.data, validateAttempt),
    [validation.data, validateAttempt],
  );
  const completionHold = useMemo(
    () => deriveRetryAfterHold(completion.data, completionAttempt),
    [completion.data, completionAttempt],
  );
  const validateRetryHeld = validateHold !== null && validateHold !== releasedValidateHold;
  const completionSubmitHeld = completionHold !== null && completionHold !== releasedCompletionHold;

  // Commit-phase destructive claim (F4): only a COMMITTED mount consumes
  // the one-read slot. Defined FIRST so it runs before the validate effect
  // in the same commit — legacy destructive reads observe null once this
  // mount owns the handoff. Idempotent for the owning ticket, so
  // StrictMode's dev double-effect re-presenting the same ticket remains
  // owner. A failed claim means a newer mount replaced this render's
  // staging (its holder is already scrubbed): fail closed to lost-session
  // instead of validating with a dead holder.
  useEffect(() => {
    if (holderRef.current === null) {
      return;
    }
    if (commitFirstCredentialHandoffClaim(claimTicketRef.current)) {
      claimOwnedRef.current = true;
    } else if (!claimOwnedRef.current) {
      scrubFirstCredentialHandoffHolder(holderRef.current);
      holderRef.current = null;
      setHandoffPresent(false);
    }
  }, []);

  // Non-consuming readiness check; fires exactly once, after the claim.
  //
  // #141 R1: the mutate call is deferred to a cancellable microtask instead of
  // running synchronously in the effect body. StrictMode replays every mount
  // effect (cleanup→setup) synchronously inside the same flush, and a mutate
  // issued before that replay settles is broken twice over: the unmount
  // cleanup below runs `abortInFlightRequests()` against the freshly armed
  // controller (TanStack awaits onMutate before mutationFn, so the fetch
  // starts already aborted → AbortError → lost-session on every dev visit),
  // and useMutation's useSyncExternalStore unsubscribe detaches the observer
  // from the in-flight mutation while the re-subscribe never re-attaches it
  // (query-core `MutationObserver` has no `onSubscribe`), so even an
  // un-aborted result lands on a mutation nobody observes and the page stays
  // on the spinner. One microtask later there is nothing to abort during the
  // replay, and the surviving setup's task runs after the final subscription
  // is in place. The one-shot marker moves into the task so the replayed
  // setup still fires it exactly once.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled || validateStartedRef.current) {
        return;
      }
      validateStartedRef.current = true;
      const authorization = holderRef.current?.authorization ?? null;
      if (authorization !== null) {
        setValidateAttempt((attempt) => attempt + 1);
        validateMutate({ authorization, signal: armAbort(validateAbortRef) });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [armAbort, validateMutate]);

  // Unmount: the claimed authorization dies with this page; evict the
  // mutation-cache entries that hold it (validate/complete variables) and
  // drop the held idempotency key. The module holder scrub is a
  // generation-guarded MICROTASK, not a timer: microtasks run when the
  // current JS stack empties and cannot be throttled by a backgrounded/
  // suspended tab the way setTimeout can, so a real unmount's scrub is
  // bounded. StrictMode's simulated remount replays cleanup→setup
  // SYNCHRONOUSLY within the same effects flush — before any queued
  // microtask runs — so the paired setup advances the generation first and
  // the stale cleanup's scrub no-ops, leaving the live holder intact. A
  // real unmount has no successor setup: its generation stays current and
  // the microtask scrubs immediately. Each cleanup captures ITS OWN holder
  // and generation, so a late scrub can never touch a newer mount's holder
  // (a new fiber has its own generation ref and holder object).
  useEffect(() => {
    holderScrubGenerationRef.current += 1;
    return () => {
      const holder = holderRef.current;
      const generation = holderScrubGenerationRef.current;
      queueMicrotask(() => {
        if (generation === holderScrubGenerationRef.current) {
          scrubFirstCredentialHandoffHolder(holder);
        }
      });
      // #141 R2: the held request object is still referenced by the pending
      // transport call — overwrite its secrets in place before dropping it.
      scrubLastSubmission();
      abortInFlightRequests();
      resetIdempotencyKeys();
      purgeMutationCache();
    };
  }, [resetIdempotencyKeys, purgeMutationCache, abortInFlightRequests, scrubLastSubmission]);

  // Shared synchronous secure-session invalidation (T036 rounds 3–4). Scrubs
  // every page-owned copy of the secrets and flips the flow to lost-session:
  // the holder (in place), typed fields, inline/summary error state, the
  // held submission (fields overwritten, then dropped), the held idempotency
  // key, the terminal/completed snapshots (addendum L63: a browser restore
  // renders lost-session UNCONDITIONALLY, never a stale completed/terminal
  // panel), and both mutations — reset() detaches the observers from any
  // in-flight mutation, so a late settlement from the pre-hide generation
  // notifies no one and can never resurrect the form, and the purge evicts
  // the cached variables holding fc1/password.
  const invalidateSecureSession = useCallback((): void => {
    // FIRST, before anything can yield: stale effect closures from a render
    // committed before this call must observe the invalidation and no-op.
    sessionInvalidatedRef.current = true;
    scrubFirstCredentialHandoffHolder(holderRef.current);
    holderRef.current = null;
    setHandoffPresent(false);
    clearSecretFields();
    setConfirmationMismatch(false);
    setPasswordServerError(null);
    setValidationFailedSnapshot(null);
    setValidateRateLimitCtx(null);
    setValidateOutageCtx(null);
    setTerminal(null);
    setCompletedSnapshot(false);
    scrubLastSubmission();
    // Abort BEFORE reset(): reset detaches the observers first, so the
    // AbortError rejection the transport produces notifies no one (never
    // the uncertain-outcome alert), and the request body stops travelling.
    abortInFlightRequests();
    resetIdempotencyKeys();
    validateReset();
    completionReset();
    purgeMutationCache();
  }, [
    clearSecretFields,
    scrubLastSubmission,
    abortInFlightRequests,
    resetIdempotencyKeys,
    validateReset,
    completionReset,
    purgeMutationCache,
  ]);

  // BFCache lifecycle (T036 round 3 finding 1; round 4 finding 1). The
  // module's pagehide listener scrubs MODULE memory as the page enters the
  // cache, but a FROZEN BFCache entry would otherwise still retain the
  // page-owned secrets (React state + DOM values, lastSubmissionRef, the
  // held key, mutation variables) until a restore — and an evicted entry
  // never fires pageshow, so restoration-time cleanup alone cannot bound
  // retention. So: invalidate synchronously ON pagehide (unconditionally —
  // on a real unload the memory dies anyway, harmless), AND again on
  // pageshow with persisted=true as defense in depth — the restore must
  // still flip the RENDER to lost-session, because pagehide-time setState
  // cannot repaint a page that freezes immediately afterward (the
  // synchronous ref/holder/cache scrubbing is what pagehide guarantees).
  useEffect(() => {
    const onPageHide = (): void => {
      invalidateSecureSession();
    };
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) {
        return;
      }
      invalidateSecureSession();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [invalidateSecureSession]);

  // Pre-write validation failure (addendum "Validation failed" row; form
  // contract: "After a pre-write validation failure, clear both fields and
  // generate a new key when the user edits"). Shared by the server
  // validation_failed outcome and the local pre-write gates (over-length,
  // leading/trailing whitespace) so all three fail identically: both
  // uncontrolled password fields are overwritten in place, the
  // confirmation-mismatch flag is dropped (cleared fields carry no
  // confirmation error — the inline password error and the focused summary
  // carry the message), the allowlisted, non-secret error content is
  // snapshotted for the inline error + focused summary (UI contract: "Inline
  // password error plus focused page summary"), the held submission's secrets
  // are scrubbed, and the completion mutation — whose variables hold the fc1
  // authorization and the rejected password — is reset and evicted from the
  // cache immediately. The validation mutation survives: its `ready` result
  // carries the server password policy the still-live form renders.
  //
  // The idempotency holder is deliberately NOT touched here. Its key is
  // digest-keyed (#141 R29): retyping the IDENTICAL payload reproduces the
  // same digest and therefore the same key — a safe replay if an earlier
  // write of that payload was uncertain — while an EDITED payload yields a
  // fresh key on its own. Clearing the field values is therefore fully
  // compatible with the replay constraint; callers that know the server
  // consumed the key (validation_failed) reset it themselves.
  const failPreWriteValidation = useCallback(
    (message: string, fieldErrors: ValidationFailedSnapshot["fieldErrors"]): void => {
      clearSecretFields();
      setConfirmationMismatch(false);
      setPasswordServerError(
        fieldErrors.find((fieldError) => fieldError.field === "password")?.message ?? message,
      );
      setValidationFailedSnapshot({ message, fieldErrors });
      scrubLastSubmission();
      completionReset();
      purgeCompletionMutations();
    },
    [clearSecretFields, scrubLastSubmission, completionReset, purgeCompletionMutations],
  );

  // Server-side pre-write rejection: the server has seen (and consumed) the
  // held key for this payload, so the next submission is a new logical
  // operation — the holder is dropped in addition to the shared handling.
  useEffect(() => {
    if (completion.data?.outcome === "validation_failed") {
      failPreWriteValidation(completion.data.message, completion.data.fieldErrors);
      resetIdempotencyKeys();
    }
  }, [completion.data, failPreWriteValidation, resetIdempotencyKeys]);

  // Thrown (uncertain) validation error: the flow ends in the lost-session
  // state, so the claimed fc1 must not linger in the holder or in the
  // validation mutation's variables until unmount — scrub the holder IN
  // PLACE (both fiber buffers reference it), the held submission/key, both
  // observers, and this feature's whole mutation-cache scope the moment the
  // panel renders.
  useEffect(() => {
    if (!validation.isError) {
      return;
    }
    scrubFirstCredentialHandoffHolder(holderRef.current);
    setHandoffPresent(false);
    scrubLastSubmission();
    resetIdempotencyKeys();
    validateReset();
    completionReset();
    purgeMutationCache();
  }, [
    validation.isError,
    scrubLastSubmission,
    resetIdempotencyKeys,
    validateReset,
    completionReset,
    purgeMutationCache,
  ]);

  // UI contract lifecycle: success clears sensitive in-memory state exactly
  // like a terminal state — the claimed fc1 holder (scrubbed in place so
  // neither fiber buffer retains it), typed fields, the pending submission
  // (fc1 + password), the held idempotency key, the mutation-observer
  // results (variables hold fc1/password), and this feature's
  // mutation-cache entries — the moment the completed panel renders, not
  // only on unmount.
  useEffect(() => {
    // Post-invalidation resurrection guard: this effect may run with a stale
    // closure after invalidateSecureSession (see sessionInvalidatedRef).
    if (sessionInvalidatedRef.current) {
      return;
    }
    if (completedSnapshot || completion.data?.outcome !== "completed") {
      return;
    }
    setCompletedSnapshot(true);
    scrubFirstCredentialHandoffHolder(holderRef.current);
    setHandoffPresent(false);
    clearSecretFields();
    setConfirmationMismatch(false);
    scrubLastSubmission();
    resetIdempotencyKeys();
    validateReset();
    completionReset();
    purgeMutationCache();
  }, [
    completion.data,
    completedSnapshot,
    clearSecretFields,
    scrubLastSubmission,
    resetIdempotencyKeys,
    validateReset,
    completionReset,
    purgeMutationCache,
  ]);

  // T029: the moment a terminal outcome arrives, snapshot its discriminator
  // and correlation reference and immediately clear every in-memory
  // sensitive value — typed password fields, the pending submission (fc1 +
  // password), the held idempotency key, and this feature's mutation-cache
  // entries. Clearing happens when the terminal panel renders, not only on
  // unmount. Retryable pre-decision classes (rate_limited /
  // dependency_unavailable) never reach this effect: their authority is
  // unchanged and the fc1 must survive for the bounded user retry.
  useEffect(() => {
    // Post-invalidation resurrection guard (same race as the completed
    // snapshot above): never re-create a terminal panel after invalidation.
    if (sessionInvalidatedRef.current) {
      return;
    }
    if (terminal) {
      return;
    }
    const completionResult = completion.data;
    const validationResult = validation.data;
    const denied: TerminalSnapshot | undefined =
      completionResult && !NON_TERMINAL_COMPLETION_OUTCOMES.includes(completionResult.outcome)
        ? {
            outcome: completionResult.outcome as TerminalOutcome,
            correlationId: completionResult.correlationId,
          }
        : validationResult && !NON_TERMINAL_VALIDATION_OUTCOMES.includes(validationResult.outcome)
          ? {
              outcome: validationResult.outcome as TerminalOutcome,
              correlationId: validationResult.correlationId,
            }
          : undefined;
    if (!denied) {
      return;
    }
    setTerminal(denied);
    scrubFirstCredentialHandoffHolder(holderRef.current);
    setHandoffPresent(false);
    clearSecretFields();
    setConfirmationMismatch(false);
    scrubLastSubmission();
    resetIdempotencyKeys();
    validateReset();
    completionReset();
    purgeMutationCache();
  }, [
    completion.data,
    validation.data,
    terminal,
    clearSecretFields,
    scrubLastSubmission,
    resetIdempotencyKeys,
    validateReset,
    completionReset,
    purgeMutationCache,
  ]);

  // Validate-time rate-limit latch (parent RateLimitPanel pattern): the
  // wait-and-retry panel stays mounted while a retry is pending. The hold
  // itself is derived above; this effect only tracks the latch context.
  useEffect(() => {
    const result = validation.data;
    if (result?.outcome === "rate_limited" || result?.outcome === "dependency_unavailable") {
      setValidateRateLimitCtx(
        result.outcome === "rate_limited" ? { correlationId: result.correlationId } : null,
      );
    } else if (result) {
      setValidateRateLimitCtx(null);
    }
  }, [validation.data]);

  // Release timer for the derived validate hold: re-enables the held retry
  // when the advertised deadline passes — arming the hold never depends on
  // this effect. BOTH retryable pre-decision classes may carry the optional
  // hint (the OpenAPI marks the header optional on 429 AND 503), so the hold
  // covers the dependency_unavailable panel too. When the timer fires it
  // re-checks the absolute deadline and RESCHEDULES if time remains (a
  // clock moved backward must not land the release marker early — the
  // handler deadline guards would then silently swallow activations of an
  // enabled-looking control), releasing only at/after the true deadline
  // (parent registration-page releaseAtDeadline pattern).
  useEffect(() => {
    if (validateHold === null) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const releaseAtDeadline = (): void => {
      const remainingMs = validateHold.deadline - Date.now();
      if (remainingMs <= 0) {
        setReleasedValidateHold(validateHold);
        return;
      }
      timer = setTimeout(releaseAtDeadline, Math.min(remainingMs, RETRY_AFTER_MAX_SECONDS * 1000));
    };
    releaseAtDeadline();
    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [validateHold]);

  // Validate-time dependency-outage latch (parent SupportPanel pattern):
  // pre-decision outage leaves the fc1 usable, so the panel offers a bounded
  // user-activated retry instead of a terminal restart.
  useEffect(() => {
    const result = validation.data;
    if (result?.outcome === "dependency_unavailable") {
      setValidateOutageCtx({ correlationId: result.correlationId });
    } else if (result) {
      setValidateOutageCtx(null);
    }
  }, [validation.data]);

  // Completion Retry-After release timer: a rate-limited or
  // dependency-unavailable completion keeps the form/outage panel, the fc1,
  // and the held idempotency key intact; the derived hold above disables
  // submission (and the outage panel's replay) for the advertised window,
  // and this effect re-enables it once the deadline passes (no write
  // occurred); an absent hint omits the hold. Same clock-backwards-safe
  // re-check-and-reschedule release as the validate hold above.
  useEffect(() => {
    if (completionHold === null) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const releaseAtDeadline = (): void => {
      const remainingMs = completionHold.deadline - Date.now();
      if (remainingMs <= 0) {
        setReleasedCompletionHold(completionHold);
        return;
      }
      timer = setTimeout(releaseAtDeadline, Math.min(remainingMs, RETRY_AFTER_MAX_SECONDS * 1000));
    };
    releaseAtDeadline();
    return () => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [completionHold]);

  const ready = validation.data?.outcome === "ready" ? validation.data : undefined;
  const validationFailed = validationFailedSnapshot;
  // Retryable pre-security-decision denials: dependency outage keeps the
  // held submission and idempotency key in memory for the user's explicit
  // replay of the identical logical operation; rate limiting keeps the form
  // with a Retry-After hold on submission.
  const completionOutage =
    completion.data?.outcome === "dependency_unavailable" ? completion.data : undefined;
  const completionRateLimited =
    completion.data?.outcome === "rate_limited" ? completion.data : undefined;
  const validateRateLimited =
    validation.data?.outcome === "rate_limited" ||
    (validateRateLimitCtx !== null && validation.isPending);
  const validateInOutage =
    validation.data?.outcome === "dependency_unavailable" ||
    (validateOutageCtx !== null && validation.isPending);

  const phase = ((): string => {
    // Terminal/completed snapshots precede the lost-session check: both
    // scrub the holder, which must not flip the UI to lost-session.
    if (terminal) {
      return `denied:${terminal.outcome}`;
    }
    if (completedSnapshot) {
      return "completed";
    }
    if (!handoffPresent) {
      return "lost";
    }
    if (completionOutage) {
      return "outage";
    }
    if (validateRateLimited) {
      return "rate-limited:validate";
    }
    if (validateInOutage) {
      return "support:validate";
    }
    if (ready) {
      return "form";
    }
    if (validation.isError) {
      return "lost";
    }
    return "checking";
  })();

  // A phase change moves focus to the new page heading so keyboard and
  // screen-reader users land on the state transition (UI-contract
  // accessibility, same mechanism as the registration page).
  useEffect(() => {
    if (phase === "checking") {
      return;
    }
    contentRef.current?.querySelector<HTMLElement>("h1")?.focus();
  }, [phase]);

  useEffect(() => {
    if (validationFailedSnapshot) {
      validationAlertRef.current?.focus();
    }
  }, [validationFailedSnapshot]);

  useEffect(() => {
    if (completion.isError) {
      uncertainAlertRef.current?.focus();
    }
  }, [completion.isError]);

  useEffect(() => {
    if (completion.data?.outcome === "rate_limited") {
      rateLimitAlertRef.current?.focus();
    }
  }, [completion.data]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const authorization = holderRef.current?.authorization ?? null;
    if (authorization === null || completion.isPending || completionSubmitHeld) {
      return;
    }
    // Read the uncontrolled fields ONCE, at submit (see the ref declarations).
    const password = passwordInputRef.current?.value ?? "";
    const confirmation = confirmationInputRef.current?.value ?? "";
    if (password !== confirmation) {
      // Client-only confirmation: a mismatch never leaves the browser. Any
      // stale summary from a previous failed submit (e.g. over-length) is
      // cleared so old destructive guidance never lingers next to the new
      // inline mismatch error.
      setConfirmationMismatch(true);
      setValidationFailedSnapshot(null);
      return;
    }
    setConfirmationMismatch(false);
    // Contract bound (PasswordValue maxLength): JSON Schema maxLength counts
    // Unicode CODE POINTS, so the authoritative gate must too —
    // `password.length` counts UTF-16 units and would wrongly block valid
    // astral-character passwords. Same addendum validation-failed handling as
    // a server validation failure (clear both fields, inline password error
    // plus focused page summary); an over-length value — typed or pasted,
    // never truncated — never reaches the wire. The snapshot replaces any
    // stale server validation summary so conflicting old guidance never
    // lingers, and the reset/purge drops a latched completion alert (429
    // latch or uncertain-retry) that would otherwise stay mounted above the
    // focused summary describing an attempt that was never sent (V21). Safe
    // here: an active Retry-After hold makes this branch unreachable
    // (completionSubmitHeld gates submission above), so dropping
    // completion.data cannot release a live hold. The idempotency holder is
    // NOT reset: this rejection never reached the server, so an uncertain
    // first write is still unresolved and the contract keeps its key until
    // that operation resolves (the holder retains only a digest, never the
    // plaintext payload; see failPreWriteValidation).
    if (Array.from(password).length > PASSWORD_MAX_LENGTH) {
      const overLengthMessage = `Your password must be ${String(PASSWORD_MAX_LENGTH)} characters or fewer.`;
      failPreWriteValidation(overLengthMessage, [
        { field: "password", code: "password_requirements_not_met", message: overLengthMessage },
      ]);
      return;
    }
    // Server-advertised policy mirror: the descriptor flag is optional (pre-flag
    // descriptors omit it), so the gate only runs when the server says it
    // enforces the rule. The regex is the server's own check (leading OR
    // trailing Unicode whitespace; interior whitespace stays allowed). The
    // value is never trimmed or normalised — a passing password is sent
    // verbatim. Same validation-failed handling as the over-length gate above.
    if (
      ready !== undefined &&
      policyDisallowsEdgeWhitespace(ready.passwordPolicy) &&
      /^\s|\s$/u.test(password)
    ) {
      const whitespaceMessage = PASSWORD_EDGE_WHITESPACE_MESSAGE;
      failPreWriteValidation(whitespaceMessage, [
        { field: "password", code: "password_requirements_not_met", message: whitespaceMessage },
      ]);
      return;
    }
    setPasswordServerError(null);
    setValidationFailedSnapshot(null);
    // Exactly the contract request body: authorization + password.
    const request: FirstCredentialCompleteRequest = {
      authorization,
      password,
    };
    const fingerprint = digestFingerprint(request);
    // A new payload supersedes any previously held request object: scrub it
    // in place before the ref is repointed so its copy of the secrets does
    // not outlive the replay window it belonged to.
    scrubLastSubmission();
    lastSubmissionRef.current = { request, fingerprint };
    setCompletionAttempt((attempt) => attempt + 1);
    completionMutate({
      request,
      idempotencyKey: idempotencyKeys.keyFor(fingerprint),
      signal: armAbort(completionAbortRef),
    });
  };

  // Uncertain outcome retry: same unchanged payload, same held idempotency
  // key, so the server replays the first write instead of double-writing.
  const retryLastSubmission = (): void => {
    const last = lastSubmissionRef.current;
    if (!last || completion.isPending || completionSubmitHeld) {
      return;
    }
    // Holder liveness gate (T036 round 3, finding 1): the held submission
    // must never be replayed after the claimed authorization was scrubbed
    // (pagehide/BFCache, terminal path) — the authority is gone, so drop
    // the replayable secrets and fail closed to lost-session instead of
    // resending them.
    if (holderRef.current?.authorization == null) {
      scrubLastSubmission();
      resetIdempotencyKeys();
      setHandoffPresent(false);
      return;
    }
    // Deadline enforced in the handler too (fail-closed): even if a release
    // marker landed early, no request fires before the advertised window.
    if (completionHold !== null && Date.now() < completionHold.deadline) {
      return;
    }
    setCompletionAttempt((attempt) => attempt + 1);
    completionMutate({
      request: last.request,
      idempotencyKey: idempotencyKeys.keyFor(last.fingerprint),
      signal: armAbort(completionAbortRef),
    });
  };

  // Bounded validate retry: the fc1 authority is unchanged by the retryable
  // pre-decision classes, so the same claimed authorization is re-checked on
  // explicit user activation only.
  const retryValidation = (): void => {
    const authorization = holderRef.current?.authorization ?? null;
    if (authorization === null || validation.isPending || validateRetryHeld) {
      return;
    }
    // Deadline enforced in the handler too (fail-closed), mirroring
    // retryLastSubmission.
    if (validateHold !== null && Date.now() < validateHold.deadline) {
      return;
    }
    setValidateAttempt((attempt) => attempt + 1);
    validateMutate({ authorization, signal: armAbort(validateAbortRef) });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* FR-009.1: full public chrome (branded header + shared footer),
          matching PublicAuthCard (auth-routes.tsx). */}
      <PublicHeader />
      {/* 011-24: solid clm-primary-blue backdrop behind a centered white card
          with the shared brand header — continuous with PublicAuthCard
          (auth-routes.tsx) so the /register → /set-password → /login journey
          reads as one branded surface. */}
      <main className="clm-page-gutter flex flex-1 items-center justify-center bg-clm-primary-blue py-12">
        <Card className="w-full max-w-lg border-0 bg-white px-2 py-8 shadow-md sm:px-8">
          <div ref={contentRef} className="px-6">
            <PublicBrandHeader className="mb-8" />
            {renderPageState()}
          </div>
        </Card>
      </main>
      <AppFooter />
    </div>
  );

  function renderPageState(): ReactElement {
    // Snapshot checks come first: success/terminal scrub the holder, so
    // the lost-session check below must not shadow their panels.
    if (terminal) {
      return (
        <TerminalOutcomePanel outcome={terminal.outcome} correlationId={terminal.correlationId} />
      );
    }

    if (completedSnapshot) {
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            Your password is set
          </h1>
          {/* role="status": success confirmation, not an alert. */}
          <Alert role="status" variant="success" className="mt-4">
            <AlertTitle>Password set successfully</AlertTitle>
            <AlertDescription>
              You can now sign in with your new password whenever you are ready.
            </AlertDescription>
          </Alert>
          {/* User-activated only — never an automatic redirect. */}
          <Button asChild className="mt-4 w-full">
            <Link to="/login">Sign in</Link>
          </Button>
        </section>
      );
    }

    if (!handoffPresent || validation.isError) {
      return <LostSessionPanel />;
    }

    if (completionOutage) {
      return (
        <section>
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            Password setup is temporarily unavailable
          </h1>
          <Alert role="status" variant="warning" className="mt-4">
            <AlertTitle>Nothing was changed</AlertTitle>
            <AlertDescription>
              <p>
                This step did not go through and nothing was changed. It is safe to try the same
                request again in a moment.
              </p>
              {/* Bounded safe retry: explicit activation only, replaying the
                  identical body with the held idempotency key, held for any
                  advertised Retry-After window (optional on the 503). */}
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={retryLastSubmission}
                disabled={completion.isPending || completionSubmitHeld}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        </section>
      );
    }

    if (validateRateLimited) {
      // Retryable pre-decision throttle: the fc1 authority is unchanged, so
      // the panel offers a bounded user-activated retry after the
      // Retry-After hold instead of a terminal restart (parent parity).
      return (
        <RetryablePanel
          heading="Too many attempts right now"
          body="Please wait a moment, then try again."
          stillBlockedMessage="Still too many attempts. Please wait a moment before trying again."
          correlationId={
            validation.data?.outcome === "rate_limited"
              ? validation.data.correlationId
              : validateRateLimitCtx?.correlationId
          }
          onRetry={retryValidation}
          retryHeld={validateRetryHeld}
          retrying={validation.isPending}
        />
      );
    }

    if (validateInOutage) {
      // Retryable pre-decision outage: same authority-unchanged bounded
      // retry contract as the rate-limited class.
      return (
        <RetryablePanel
          heading="Password setup is temporarily unavailable"
          body="Nothing was changed. It is safe to try again in a moment."
          stillBlockedMessage="Password setup is still unavailable. Try again later."
          correlationId={
            validation.data?.outcome === "dependency_unavailable"
              ? validation.data.correlationId
              : validateOutageCtx?.correlationId
          }
          onRetry={retryValidation}
          retryHeld={validateRetryHeld}
          retrying={validation.isPending}
        />
      );
    }

    if (!ready) {
      return (
        <div
          className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"
          role="status"
        >
          <ClmIcon
            name="loading"
            decorative
            className="h-6 w-6 animate-spin text-clm-brand-blue motion-reduce:animate-none"
          />
          <span>Checking your secure password setup</span>
        </div>
      );
    }

    return (
      <div>
        <div className="mb-6">
          {/* 011-24 form kit: light-weight centered heading over a muted
              centered explainer (frames 706:2065 / 703:7903). */}
          <h1
            tabIndex={-1}
            className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
          >
            Set your password
          </h1>
          <p className="mt-2 text-center text-base text-clm-dark-gray">
            Choose the password you will use to sign in.
          </p>
        </div>

        {completion.isError && (
          <Alert ref={uncertainAlertRef} tabIndex={-1} variant="warning" className="mb-6">
            <AlertTitle>We couldn&apos;t confirm your password setup</AlertTitle>
            <AlertDescription>
              <p>We didn&apos;t get a response. It is safe to try the same request again.</p>
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={retryLastSubmission}
                disabled={completion.isPending}
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {completionRateLimited && (
          <Alert ref={rateLimitAlertRef} tabIndex={-1} variant="warning" className="mb-6">
            <AlertTitle>Too many attempts right now</AlertTitle>
            <AlertDescription>
              Please wait a moment, then set your password again. Nothing was changed.
            </AlertDescription>
          </Alert>
        )}

        {validationFailed && (
          <Alert ref={validationAlertRef} tabIndex={-1} variant="destructive" className="mb-6">
            <AlertTitle>Review your new password</AlertTitle>
            <AlertDescription>
              {validationFailed.fieldErrors.length > 0 ? (
                <ul className="list-inside list-disc">
                  {validationFailed.fieldErrors.map((fieldError) => (
                    <li key={`${fieldError.field}:${fieldError.code}`}>{fieldError.message}</li>
                  ))}
                </ul>
              ) : (
                <p>{validationFailed.message}</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        <form noValidate onSubmit={handleSubmit}>
          <div className="mb-4">
            <Label htmlFor="first-credential-password" className="font-bold text-clm-primary-blue">
              New password
            </Label>
            <Input
              id="first-credential-password"
              ref={passwordFieldRef}
              type="password"
              autoComplete="new-password"
              required
              defaultValue=""
              onChange={() => {
                setPasswordServerError(null);
              }}
              aria-invalid={passwordServerError !== null}
              aria-describedby={
                passwordServerError !== null
                  ? `${PASSWORD_ERROR_ID} ${PASSWORD_REQUIREMENTS_ID}`
                  : PASSWORD_REQUIREMENTS_ID
              }
            />
            {passwordServerError !== null && (
              <p id={PASSWORD_ERROR_ID} className="mt-2 text-sm text-alert-red">
                {passwordServerError}
              </p>
            )}
            <PasswordRequirements policy={ready.passwordPolicy} id={PASSWORD_REQUIREMENTS_ID} />
          </div>
          <div className="mb-6">
            <Label
              htmlFor="first-credential-confirmation"
              className="font-bold text-clm-primary-blue"
            >
              Confirm password
            </Label>
            <Input
              id="first-credential-confirmation"
              ref={confirmationFieldRef}
              type="password"
              autoComplete="new-password"
              required
              defaultValue=""
              onChange={() => {
                setConfirmationMismatch(false);
              }}
              aria-invalid={confirmationMismatch}
              aria-describedby={confirmationMismatch ? CONFIRMATION_ERROR_ID : undefined}
            />
            {confirmationMismatch && (
              <p id={CONFIRMATION_ERROR_ID} className="mt-2 text-sm text-alert-red">
                Both password fields must match.
              </p>
            )}
          </div>
          <Button
            type="submit"
            className="w-full"
            disabled={completion.isPending || completionSubmitHeld}
          >
            Set password
          </Button>
          {/* Always-mounted polite live region: announces the in-flight
              submission (UI contract Submitting row). */}
          <p role="status" className="mt-3 text-sm text-muted-foreground">
            {completion.isPending ? "Setting your password\u2026" : null}
          </p>
        </form>
      </div>
    );
  }
}

/**
 * Generic lost-session re-entry state: rendered for every path where the
 * one-read handoff is unavailable (reload, new tab, restore, double-read) or
 * the claimed authorization can no longer be used. No identity lookup, no
 * authority reconstruction — the only path forward is proof-bound
 * registration re-entry.
 */
function LostSessionPanel(): ReactElement {
  return (
    <section>
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        Your secure setup session ended
      </h1>
      <p className="mt-2 text-base text-gray-600">
        For your security, this password setup session is no longer available. Open the link from
        your registration email again to return to your completed registration and restart password
        setup from there.
      </p>
    </section>
  );
}

/**
 * Retryable pre-security-decision panel (rate limited / dependency
 * unavailable before the provider decision): authority is unchanged, so the
 * panel keeps a bounded user-activated retry honoring the Retry-After hold,
 * mirroring the parent registration page's RateLimit/Support panels. The
 * status live region is the only assistive-technology feedback when a retry
 * lands on this same panel again (WCAG 4.1.3).
 */
function RetryablePanel({
  heading,
  body,
  stillBlockedMessage,
  correlationId,
  onRetry,
  retryHeld,
  retrying,
}: {
  heading: string;
  body: string;
  stillBlockedMessage: string;
  correlationId?: string;
  onRetry: () => void;
  retryHeld: boolean;
  retrying: boolean;
}): ReactElement {
  const [retryAttempted, setRetryAttempted] = useState(false);
  return (
    <section>
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        {heading}
      </h1>
      <p className="mt-2 text-base text-gray-600">{body}</p>
      <p role="status" className="mt-2 text-sm text-gray-600">
        {retrying ? "Retrying\u2026" : retryAttempted ? stillBlockedMessage : null}
      </p>
      <Button
        type="button"
        variant="outline"
        className="mt-4"
        onClick={() => {
          setRetryAttempted(true);
          onRetry();
        }}
        disabled={retryHeld || retrying}
      >
        Try again
      </Button>
      <SupportReference correlationId={correlationId} />
    </section>
  );
}

/**
 * T029 approved terminal panels, keyed by the contract outcome
 * discriminator. Every panel is heading-first (focus lands on the h1 via the
 * shared phase effect), discloses no internal cause, code, or account
 * identifier, and offers only safe direction: proof-bound registration
 * re-entry, Sign in / password recovery, or support. Support/unavailable
 * panels additionally display the non-secret correlation reference the
 * contract requires. None of them exposes a retry control — the retryable
 * pre-decision classes (rate_limited / dependency_unavailable) are handled
 * before terminal snapshotting.
 */
function TerminalOutcomePanel({
  outcome,
  correlationId,
}: {
  outcome: string;
  correlationId: string;
}): ReactElement {
  if (outcome === "already_established") {
    return (
      <section>
        <h1
          tabIndex={-1}
          className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
        >
          Your password is already set
        </h1>
        <p className="mt-2 text-base text-gray-600">
          Sign in with your existing password. If you have forgotten it, you can reset it from the
          sign-in page.
        </p>
        {/* The sanctioned Hosted-UI-owned recovery affordance: one honest CTA
            into /login, where sign-in and password recovery both live. */}
        <Button asChild className="mt-4 w-full">
          <Link to="/login">Sign in / use password recovery</Link>
        </Button>
      </section>
    );
  }

  const copy = ((): { heading: string; body: string; supportReference: boolean } => {
    switch (outcome) {
      case "expired":
        return {
          heading: "This password link has expired",
          body:
            "For your security, this setup session ended. Open the link from your " +
            "registration email again to return to your completed registration and restart " +
            "password setup from there.",
          supportReference: false,
        };
      case "revoked":
        return {
          heading: "This password setup is no longer active",
          body:
            "Open the link from your registration email to restart from your completed " +
            "registration, or contact support if you need help.",
          supportReference: false,
        };
      case "support_required":
        return {
          heading: "We can't confirm your password setup right now",
          body: "Contact support to finish setting up your password.",
          supportReference: true,
        };
      case "in_progress":
        return {
          heading: "Password setup is already in progress",
          body:
            "This password setup is already being completed. If it does not finish, open the " +
            "link from your registration email to start again later, or contact support.",
          supportReference: false,
        };
      default:
        // "unavailable" and any future outcome: deliberately generic — no
        // expiry, revocation, or account-existence hint.
        return {
          heading: "Password setup cannot be completed",
          body:
            "This request did not go through. Open the link from your registration email to " +
            "start again, or contact support if you need help.",
          supportReference: true,
        };
    }
  })();

  return (
    <section>
      <h1
        tabIndex={-1}
        className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
      >
        {copy.heading}
      </h1>
      <p className="mt-2 text-base text-gray-600">{copy.body}</p>
      {copy.supportReference && <SupportReference correlationId={correlationId} />}
    </section>
  );
}

/**
 * Admin-host route for `/set-password` (CLMS-137). Password setup is a
 * standard-realm flow only: this page renders generic guidance, never claims
 * the in-memory handoff, and never calls the first-credential API.
 */
export function FirstCredentialAdminRealmPage(): ReactElement {
  // FR-009.5: same chrome/card/brand composition as the standard-realm page
  // so admin-realm guidance reads as the same branded surface.
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <PublicHeader />
      <main className="clm-page-gutter flex flex-1 items-center justify-center bg-clm-primary-blue py-12">
        <Card className="w-full max-w-lg border-0 bg-white px-2 py-8 shadow-md sm:px-8">
          <div className="px-6">
            <PublicBrandHeader className="mb-8" />
            <h1
              tabIndex={-1}
              className="text-center text-2xl font-light text-clm-dark-gray focus:outline-none"
            >
              Password setup isn&apos;t available here
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Password setup can&apos;t be used in this application. Sign out of this administrative
              session, then open your link again. Contact support if you need help.
            </p>
          </div>
        </Card>
      </main>
      <AppFooter />
    </div>
  );
}
